/**
 * "What changes" — the check every co-agency / referral / agent-split change on an
 * INVOICED sale goes through before it is saved (owner, 2026-10-09).
 *
 * HQ works the change out (POST /sales/:id/parties/preview) and this shows what it
 * found: the changes in words, each commission line now → after, how much money has
 * already come through the sale, and what HQ will do about it. When money already
 * moved in a way the change would contradict — a line paid out, a "We list"
 * co-agency already paid its cash share — HQ refuses, and this says what to undo
 * first instead of offering a Save. Otherwise a reason is required: it goes into
 * the sale's history and to the agents on the sale.
 *
 * One body, two frames, so the phone and desktop can never show a different check:
 *   PartyChangeSheet — the phone's bottom sheet (scrim, grab handle, rise-in), at
 *                      the app's sheet layer 4600 so antd toasts (6000) land on top
 *   PartyChangeModal — the desktop's antd Modal
 * Both keep the reason themselves and hand it to `onConfirm(reason)`; `onClose` is
 * "not now". Neither closes itself on a failed save — the error toast says why and
 * the reason the agent typed is still there to try again.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Input, Modal } from 'antd';
import useSheetAnim from '../hooks/useSheetAnim';
import { AsyncButton, Chip, RM } from './kit';

const near = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) < 0.005;

/**
 * Whose agent share each line comes from (`share_of_name`) — said only when the
 * lines come from two or more shares, where "HQ Royalty · HQ" would otherwise
 * appear twice with nothing to tell the rows apart. An agent is sent their own
 * lines only, so this is rare here; a server without the field shows nothing.
 */
const shareOfShown = (lines) => new Set((lines || []).map((l) => l?.share_of_name).filter(Boolean)).size >= 2;

/** Whether the preview offers a Save at all. */
export const partyChangeCanSave = (p) => !!p
  && p.can_save !== false
  && !(p.blocked || []).length
  && (p.changes || []).length > 0;

const ACTION = {
  added: { label: 'Added', tone: 'ok' },
  changed: { label: 'Changed', tone: 'brand' },
  removed: { label: 'Removed', tone: 'bad' },
  letter: { label: 'Letter', tone: 'muted' },
};

const capLabel = { fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--muted)' };
const box = { background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' };
const rowPad = { padding: '11px 13px' };
const note = (tone) => ({
  background: `color-mix(in srgb,var(--${tone}) 9%,var(--surface))`,
  border: `1px solid color-mix(in srgb,var(--${tone}) 32%,var(--border))`,
  borderRadius: 12,
  padding: '12px 13px',
  fontSize: 12.5,
  lineHeight: 1.5,
  color: 'var(--text)',
});

function Section({ label, right, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <span style={capLabel}>{label}</span>
        {right && <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

/**
 * One commission line: who, then now → after, coloured by which way it moves.
 * Every figure in this check is to the sen (RM 4,095.00), like the sentences HQ
 * writes under it — an agent compares them side by side.
 */
function LineRow({ l, last, meId, showShareOf }) {
  const before = l.before == null ? null : Number(l.before);
  const after = Number(l.after) || 0;
  const up = !l.removed && after > (before || 0) + 0.005;
  const down = l.removed || after < (before || 0) - 0.005;
  const same = !l.added && !l.removed && near(after, before);
  const tone = up ? 'var(--ok)' : down ? 'var(--bad)' : 'var(--muted)';
  const mine = l.recipient_type === 'AGENT' && meId && l.recipient_id === meId;
  return (
    <div style={{
      ...rowPad,
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 12, rowGap: 4,
      borderBottom: last ? 'none' : '1px solid var(--border)',
      background: mine ? 'color-mix(in srgb,var(--primary) 6%,transparent)' : 'transparent',
    }}>
      <div style={{ flex: '1 1 150px', minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35, color: same ? 'var(--muted)' : 'var(--text)' }}>{l.label}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <span style={{ fontSize: 11.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{l.recipient_name || '—'}</span>
          {showShareOf && l.share_of_name && l.share_of_name !== l.recipient_name && (
            <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>· from {l.share_of_name}&rsquo;s share</span>
          )}
          {mine && <Chip label="You" tone="brand" style={{ fontSize: 10.5, padding: '1px 7px' }} />}
          {l.added && <Chip label="New" tone="ok" style={{ fontSize: 10.5, padding: '1px 7px' }} />}
          {l.removed && <Chip label="Removed" tone="bad" style={{ fontSize: 10.5, padding: '1px 7px' }} />}
          {l.paid_locked && <Chip label={`Paid out ${RM(l.paid)}`} tone="warn" style={{ fontSize: 10.5, padding: '1px 7px' }} />}
        </div>
      </div>
      <div style={{ marginLeft: 'auto', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontSize: 12.5 }}>
        {same ? (
          <span style={{ color: 'var(--muted)' }}>{RM(after)} · no change</span>
        ) : (
          <>
            <span style={{ color: 'var(--muted)' }}>{before == null ? '—' : RM(before)}</span>
            <span style={{ color: 'var(--muted)', margin: '0 5px' }}>→</span>
            <b style={{ color: tone }}>{RM(l.removed ? 0 : after)}</b>
            <span style={{ color: tone, fontSize: 10, marginLeft: 4 }}>{up ? '▲' : '▼'}</span>
          </>
        )}
      </div>
    </div>
  );
}

/** The check itself — no frame, no buttons. */
export default function PartyChangeReview({ preview: p, meId, reason, onReason }) {
  if (!p) return null;
  const changes = p.changes || [];
  const blocked = p.blocked || [];
  const lines = p.lines || [];
  const points = p.points || [];
  const money = p.money || null;
  const canSave = partyChangeCanSave(p);
  const showShareOf = shareOfShown(lines);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Section label="WHAT CHANGES">
        {changes.length ? (
          <div style={box}>
            {changes.map((c, i) => {
              const a = ACTION[c.action] || ACTION.changed;
              return (
                <div key={`${c.what}-${c.id}-${c.action}-${i}`} style={{ ...rowPad, display: 'flex', alignItems: 'flex-start', gap: 10, borderBottom: i < changes.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <Chip label={a.label} tone={a.tone} style={{ flex: 'none', fontSize: 10.5, padding: '2px 8px', marginTop: 1 }} />
                  <span style={{ fontSize: 13, lineHeight: 1.45, minWidth: 0 }}>{c.text}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            HQ sees no difference — the co-agencies, referrals and splits are as they were. There is nothing to save.
          </div>
        )}
      </Section>

      {blocked.length > 0 ? (
        <>
          {blocked.map((b, i) => (
            <div key={`${b.code}-${i}`} role="alert" style={note('bad')}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--bad)', marginBottom: 3 }}>This change can&rsquo;t be saved yet</div>
              {b.message}
            </div>
          ))}
          <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            Nothing has been saved. Close this and put the co-agencies and splits back, or ask HQ to undo what is described above first.
          </div>
        </>
      ) : changes.length > 0 && (
        <>
          {p.hand_set && (
            <div style={note('warn')}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--warn)', marginBottom: 3 }}>Commission set by hand</div>
              HQ finance set this sale&rsquo;s commission by hand, so it is not recalculated with this change.
              HQ finance is told and decides what to do with the lines.
            </div>
          )}

          {lines.length > 0 && (
            <Section label="COMMISSION" right="Now → After">
              <div style={box}>
                {lines.map((l, i) => <LineRow key={l.id || `${l.commission_type}-${l.recipient_id}-${i}`} l={l} meId={meId} showShareOf={showShareOf} last={i === lines.length - 1} />)}
              </div>
            </Section>
          )}

          {(money || points.length > 0) && (
            <Section label="MONEY ALREADY THROUGH THIS SALE">
              <div style={{ ...box, ...rowPad, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {money && (
                  <div style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {RM(money.collected)} <span style={{ color: 'var(--muted)', fontWeight: 500 }}>of {RM(money.invoiced)} collected</span>
                  </div>
                )}
                {points.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {points.map((t, i) => <li key={i} style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text)' }}>{t}</li>)}
                  </ul>
                )}
              </div>
            </Section>
          )}

          {canSave && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label htmlFor="party-change-reason" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>
                <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>Why is this changing?
              </label>
              <Input.TextArea
                id="party-change-reason"
                value={reason}
                onChange={(e) => onReason(e.target.value)}
                autoSize={{ minRows: 2, maxRows: 5 }}
                maxLength={500}
                placeholder="What happened — e.g. a co-agent joined the deal at viewing"
              />
              <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Kept in the sale&rsquo;s history — HQ and the agents on the sale see it.</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// The frames hold on to the last preview so the exit animation still has something
// to draw after the page clears it.
function useLast(preview) {
  const last = useRef(preview);
  if (preview) last.current = preview;
  return preview || last.current;
}

function useReason(open, preview) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open, preview]);
  return [reason, setReason];
}

/** Phone — a bottom sheet in the app's own sheet chrome. */
export function PartyChangeSheet({ open, preview, meId, onClose, onConfirm, onError }) {
  const { mounted, closing } = useSheetAnim(open);
  const p = useLast(preview);
  const [reason, setReason] = useReason(open, preview);
  const [busy, setBusy] = useState(false);
  const canSave = partyChangeCanSave(p);
  const close = () => { if (!busy) onClose?.(); };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!mounted || !p) return null;

  const save = async () => {
    setBusy(true);
    try { await onConfirm(reason.trim()); } finally { setBusy(false); }
  };

  return createPortal(
    <div
      onClick={close}
      className={`sheet-scrim${closing ? ' is-closing' : ''}`}
      style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="What changes"
        className={`sheet-panel${closing ? ' is-closing' : ''}`}
        style={{ background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '90%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <div style={{ padding: '8px 0 2px', display: 'flex', justifyContent: 'center', flex: 'none' }}>
          <span style={{ width: 38, height: 4, borderRadius: 2, background: 'var(--border)' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 16px 10px', borderBottom: '1px solid var(--border)', flex: 'none' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>What changes</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>
              {canSave ? 'Worked out by HQ — nothing is saved until you press Save change' : 'Worked out by HQ — nothing has been saved'}
            </div>
          </div>
          <div
            onClick={close}
            role="button"
            tabIndex={0}
            aria-label="Close"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); close(); } }}
            style={{ width: 32, height: 32, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)', flex: 'none' }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" /></svg>
          </div>
        </div>

        <div className="scrx" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 16px 16px', WebkitOverflowScrolling: 'touch' }}>
          <PartyChangeReview preview={p} meId={meId} reason={reason} onReason={setReason} />
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '10px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', flex: 'none' }}>
          {canSave ? (
            <>
              <Button size="large" style={{ height: 46 }} onClick={close} disabled={busy}>Cancel</Button>
              <AsyncButton type="primary" size="large" block style={{ height: 46 }} disabled={!reason.trim()} onClick={save} onError={onError}>
                Save change
              </AsyncButton>
            </>
          ) : (
            <Button size="large" block style={{ height: 46 }} onClick={close}>Close</Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Desktop — the same check in an antd Modal. */
export function PartyChangeModal({ open, preview, meId, onClose, onConfirm, onError }) {
  const p = useLast(preview);
  const [reason, setReason] = useReason(open, preview);
  const [busy, setBusy] = useState(false);
  const canSave = partyChangeCanSave(p);
  const close = () => { if (!busy) onClose?.(); };
  const save = async () => {
    setBusy(true);
    try { await onConfirm(reason.trim()); } finally { setBusy(false); }
  };
  return (
    <Modal
      open={open}
      title="What changes"
      width={620}
      centered
      destroyOnHidden
      maskClosable={false}
      onCancel={close}
      footer={canSave ? [
        <Button key="c" onClick={close} disabled={busy}>Cancel</Button>,
        <AsyncButton key="s" type="primary" disabled={!reason.trim()} onClick={save} onError={onError}>Save change</AsyncButton>,
      ] : [<Button key="c" onClick={close}>Close</Button>]}
      styles={{ body: { maxHeight: '68vh', overflowY: 'auto', paddingRight: 4 } }}
    >
      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 14 }}>
        {canSave ? 'Worked out by HQ — nothing is saved until you press Save change.' : 'Worked out by HQ — nothing has been saved.'}
      </div>
      {p && <PartyChangeReview preview={p} meId={meId} reason={reason} onReason={setReason} />}
    </Modal>
  );
}
